import { inject, provide } from 'vue';

import {
	agencyWorkerStatusActionsList,
	workerStatusActionsList,
	workerStatusActionsSymbol,
} from '@/composables/missions/worker-status-actions/constant';
import i18n from '@/i18n';

import type { WorkerMissionStatus } from '@/types/mission';

import {
	type DropdownGeneratorAction,
	DropdownGeneratorActionType,
	DropdownItemColors,
} from '@/dsComponents/Dropdown/types';

export function useWorkerStatusActions() {
	const workerStatusActionsProvider = (
		fn: (status: WorkerMissionStatus) => void
	) => provide(workerStatusActionsSymbol, fn);

	const handleActionToParent = inject(workerStatusActionsSymbol, undefined);

	const workerStatusActions = (
		availableStatuses: WorkerMissionStatus[],
		onAction?: (status: WorkerMissionStatus) => void
	): DropdownGeneratorAction[] => {
		const handler = onAction ?? handleActionToParent;
		const agencyStatusActions = agencyWorkerStatusActionsList();
		const workerStatusActionsItems = workerStatusActionsList();
		const actions: (DropdownGeneratorAction & {
			statusName?: WorkerMissionStatus;
		})[] = [];

		actions.push({
			type: DropdownGeneratorActionType.item,
			// biome-ignore lint/suspicious/noExplicitAny: Init Biome
			statusName: 'workerDetails' as any,
			item: {
				iconLeft: 'search',
				iconLeftColor: DropdownItemColors.lightGrey,
				labelColor: DropdownItemColors.lightGrey,
				label: i18n.global.t(
					'modals.missionDetails.propositions.actions.details'
				),
				onClick: () => {
					if (handler) {
						// biome-ignore lint/suspicious/noExplicitAny: Init Biome
						handler('workerDetails' as any);
					}
				},
			},
		});

		if (availableStatuses.length === 0) {
			return actions;
		}

		actions.push({
			type: DropdownGeneratorActionType.separator,
		});

		const agencyStatusActionsFiltered = agencyStatusActions.filter((action) =>
			isActionAvailable(action, availableStatuses)
		);

		const workerStatusActionsFiltered = workerStatusActionsItems
			.filter((action) => isActionAvailable(action, availableStatuses))
			.filter((ms, index, arr) => {
				if (ms.type !== 'item' && !arr[index + 1]) {
					return false;
				}
				return true;
			});

		actions.push(...agencyStatusActionsFiltered);

		if (workerStatusActionsFiltered.length > 0) {
			const separator: DropdownGeneratorAction = {
				type: DropdownGeneratorActionType.separator,
			};

			actions.push(separator, ...workerStatusActionsFiltered);
		}

		return actions.map((action) => ({
			...action,
			item:
				action.type === 'item'
					? {
							...action.item,
							onClick: () => {
								if (handler && action.statusName) {
									handler(action.statusName);
								}
							},
						}
					: () => {},
		}));
	};

	return {
		handleActionToParent,
		workerStatusActions,
		workerStatusActionsProvider,
	};
}

function isActionAvailable(
	action: DropdownGeneratorAction & { statusName?: WorkerMissionStatus },
	availableStatuses: WorkerMissionStatus[]
): boolean {
	if (action.type !== 'item') {
		return true;
	}
	return availableStatuses.includes(
		action.statusName ?? ('' as WorkerMissionStatus)
	);
}
